import React from 'react';
import { observer } from 'mobx-react';
import { store } from 'stores/Store';
import styles from './MentionComposer.module.scss';
import MentionPanel, { MentionRow, buildMentionRows, nextExpandedCap } from './MentionPanel';
import {
  Mention,
  MentionCategory,
  findMentionTrigger,
  locateMentions,
  mentionEndingAt,
  mentionStartingAt,
  mentionTokenText,
  pruneMentions,
  removeMentionFromText,
} from './mentionUtils';

/**
 * Style properties the mirror div copies from the textarea so the tag
 * highlights sit exactly behind their text.
 *
 * Read at runtime rather than duplicated in SCSS, because the two disagree:
 * ChatbotV2 swaps textareas at width 600 but the SCSS breakpoint is 480, so
 * between those widths the mobile textarea renders without its media-query
 * styles. Copying computed values is correct by construction and survives any
 * later edit to ChatbotV2.module.scss.
 *
 * whiteSpace / overflowWrap / wordBreak matter most: get them wrong and every
 * line after the first is offset.
 */
const MIRRORED_STYLES = [
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
  'letterSpacing', 'wordSpacing', 'textTransform', 'textIndent', 'textAlign',
  'lineHeight',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'borderStyle', 'boxSizing', 'direction', 'tabSize',
  'whiteSpace', 'overflowWrap', 'wordBreak',
];

/**
 * The @-token currently being composed.
 *
 * It owns the region [anchor, anchor + length) of the text. Before anything is
 * picked that region is "@query"; after a pick it is the resolved token. Every
 * selection just rewrites that one region, which keeps the text surgery to a
 * single slice-and-splice.
 */
interface DraftState {
  anchor: number;
  length: number;
  query: string;
  /** Stable, so re-picking replaces this draft's mention instead of adding another. */
  mentionId: string;
  /** Null until the first pick; once set, the other groups hide. */
  category: MentionCategory | null;
  values: string[];
  activeIndex: number;
  /** Per-category row cap, grown by each "+N more" click. */
  expanded: Partial<Record<MentionCategory, number>>;
}

let mentionSeq = 0;
const nextMentionId = () => `mention-${Date.now()}-${(mentionSeq += 1)}`;

interface Props {
  className?: string;
  placeholder?: string;
  /** False disables the picker; the textarea then behaves exactly as it did before. */
  enabled: boolean;
  autoFocus?: boolean;
  /** ChatbotV2's handleSubmit. Called only when the panel does not consume the key. */
  onKeyDownFallback: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const MentionComposer = observer(React.forwardRef<HTMLTextAreaElement, Props>((
  { className, placeholder, enabled, autoFocus, onKeyDownFallback },
  forwardedRef,
) => {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = React.useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = React.useState<DraftState | null>(null);

  const setRefs = React.useCallback((node: HTMLTextAreaElement | null) => {
    innerRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
  }, [forwardedRef]);

  // Read observables into locals: this component is an observer, so MobX
  // re-renders on change, and the hook dep arrays below then see real values.
  const text = store.userInput;
  const mentions = store.mentions;
  const mentionValues = store.mentionValues;

  const located = React.useMemo(() => locateMentions(text, mentions), [text, mentions]);

  const panelOpen = enabled && draft !== null;

  const { groups, rows } = React.useMemo(() => {
    if (!panelOpen || !draft) return { groups: [], rows: [] as MentionRow[] };
    return buildMentionRows(mentionValues, draft.query, draft.category, draft.expanded);
  }, [panelOpen, draft, mentionValues]);

  const activeIndex = draft && rows.length > 0
    ? Math.min(draft.activeIndex, rows.length - 1)
    : 0;

  /* ------------------------- mirror ------------------------- */

  const syncScroll = React.useCallback(() => {
    const ta = innerRef.current;
    const mirror = mirrorRef.current;
    if (!ta || !mirror) return;
    mirror.scrollTop = ta.scrollTop;
    mirror.scrollLeft = ta.scrollLeft;
  }, []);

  React.useLayoutEffect(() => {
    const ta = innerRef.current;
    const mirror = mirrorRef.current;
    if (!ta || !mirror) return;

    const computed = window.getComputedStyle(ta);
    MIRRORED_STYLES.forEach((prop) => {
      (mirror.style as any)[prop] = (computed as any)[prop];
    });
    mirror.style.width = `${ta.clientWidth}px`;
    mirror.style.height = `${ta.clientHeight}px`;

    // Programmatic value changes move scrollTop without firing a scroll event.
    syncScroll();
  });

  React.useEffect(() => {
    const onResize = () => syncScroll();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [syncScroll]);

  const mirrorSegments = React.useMemo(() => {
    const nodes: React.ReactNode[] = [];
    let cursor = 0;

    located.forEach((entry, i) => {
      if (entry.start > cursor) nodes.push(text.slice(cursor, entry.start));
      nodes.push(
        <span key={`tag-${entry.mention.id}-${i}`} className={styles.tag}>
          {text.slice(entry.start, entry.end)}
        </span>,
      );
      cursor = entry.end;
    });

    // A textarea shows a final empty line for a trailing newline; pre-wrap in a
    // div collapses it, which would leave the mirror one line short.
    nodes.push(`${text.slice(cursor)}​`);
    return nodes;
  }, [text, located]);

  /* ------------------------- editing ------------------------- */

  const commit = (nextText: string, nextMentions: Mention[], caret?: number) => {
    store.setUserInput(nextText, pruneMentions(nextText, nextMentions));
    if (caret === undefined) return;
    window.requestAnimationFrame(() => {
      const ta = innerRef.current;
      if (ta) ta.setSelectionRange(caret, caret);
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextText = event.target.value;
    const caret = event.target.selectionStart ?? nextText.length;

    // pruneMentions drops any token this edit broke, so state can never drift
    // from what is on screen.
    store.setUserInput(nextText, pruneMentions(nextText, mentions));

    if (!enabled) return;

    if (draft) {
      // Once something is picked the token is settled; typing moves on.
      if (draft.values.length > 0) {
        setDraft(null);
        return;
      }
      const stillAtAnchor = nextText.charAt(draft.anchor) === '@' && caret > draft.anchor;
      if (!stillAtAnchor) {
        setDraft(null);
        return;
      }
      const query = nextText.slice(draft.anchor + 1, caret);
      // A newline ends the mention. Spaces are allowed, so full names can be typed.
      if (query.indexOf('\n') !== -1) {
        setDraft(null);
        return;
      }
      setDraft({ ...draft, query, length: 1 + query.length, activeIndex: 0 });
      return;
    }

    // Did this edit introduce an "@" that should open the panel? The trigger and
    // the characters after it can arrive in the same event, so seed the query
    // from whatever already follows the "@" rather than assuming it is empty.
    const anchor = findMentionTrigger(text, nextText, caret);
    if (anchor !== -1) {
      const query = nextText.slice(anchor + 1, caret);
      setDraft({
        anchor,
        length: 1 + query.length,
        query,
        mentionId: nextMentionId(),
        category: null,
        values: [],
        activeIndex: 0,
        expanded: {},
      });
    }
  };

  /** Toggle one value in the draft and rewrite the draft's region of the text. */
  const applySelection = (row: MentionRow) => {
    if (!draft) return;

    const already = draft.values.indexOf(row.value) !== -1;
    const values = already
      ? draft.values.filter((v) => v !== row.value)
      : [...draft.values, row.value];

    const before = text.slice(0, draft.anchor);
    const after = text.slice(draft.anchor + draft.length);
    const withoutDraft = mentions.filter((m) => m.id !== draft.mentionId);

    // Deselecting the last value unlocks the category and puts the typed query
    // back in the text, so the composer reads the way it did before the pick
    // rather than collapsing to a bare "@".
    if (values.length === 0) {
      const restored = `@${draft.query}`;
      commit(`${before}${restored}${after}`, withoutDraft, draft.anchor + restored.length);
      setDraft({
        ...draft, length: restored.length, category: null, values: [], activeIndex: 0,
      });
      return;
    }

    const mention: Mention = { id: draft.mentionId, category: row.category, values };
    const token = mentionTokenText(mention);

    // `query` deliberately survives the pick. The token has replaced "@query" in
    // the TEXT, but the panel is still filtering by what the user typed -- they
    // are part-way through choosing several values from one search. Clearing it
    // here resets the list to every value in the category, which is only
    // obvious once "+N more" expands the cap.
    commit(`${before}${token}${after}`, [...withoutDraft, mention], draft.anchor + token.length);
    setDraft({
      ...draft,
      length: token.length,
      category: row.category,
      values,
      activeIndex: draft.activeIndex,
    });
  };

  /* ------------------------- keys ------------------------- */

  const deleteWholeToken = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
    at: 'start' | 'end',
  ): boolean => {
    const ta = event.currentTarget;
    const caret = ta.selectionStart;
    if (caret !== ta.selectionEnd) return false;

    const target = at === 'end' ? mentionEndingAt(located, caret) : mentionStartingAt(located, caret);
    if (!target) return false;

    event.preventDefault();
    const result = removeMentionFromText(text, target, mentions);
    commit(result.text, result.mentions, result.caret);
    return true;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (panelOpen && draft) {
      switch (event.key) {
        case 'ArrowDown':
          if (rows.length > 0) {
            event.preventDefault();
            setDraft({ ...draft, activeIndex: (activeIndex + 1) % rows.length });
          }
          return;
        case 'ArrowUp':
          if (rows.length > 0) {
            event.preventDefault();
            setDraft({ ...draft, activeIndex: (activeIndex - 1 + rows.length) % rows.length });
          }
          return;
        case 'Enter':
        case 'Tab': {
          // Never fall through to handleSubmit while picking -- that would send a
          // half-composed message.
          event.preventDefault();
          const row = rows[activeIndex];
          if (row) applySelection(row);
          return;
        }
        case 'Escape':
          event.preventDefault();
          setDraft(null);
          return;
        case 'Backspace':
          if (draft.query === '' && draft.values.length === 0) setDraft(null);
          break;
        default:
          break;
      }
    }

    if (enabled && !panelOpen) {
      if (event.key === 'Backspace' && deleteWholeToken(event, 'end')) return;
      if (event.key === 'Delete' && deleteWholeToken(event, 'start')) return;
    }

    onKeyDownFallback(event);
  };

  /* ------------------------- dismissal ------------------------- */

  React.useEffect(() => {
    if (!panelOpen) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[data-mention-composer]')) return;
      setDraft(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [panelOpen]);

  return (
    <div className={styles.composerWrap} data-mention-composer>
      <div ref={mirrorRef} className={styles.mirror} aria-hidden="true">
        {mirrorSegments}
      </div>

      <textarea
        {...({ enterKeyHint: 'send' } as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        ref={setRefs}
        className={`${className || ''} ${styles.textarea}`}
        placeholder={placeholder}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onScroll={syncScroll}
        autoFocus={autoFocus}
        // No aria-expanded: a textarea's implicit role is textbox, which does
        // not support it. aria-activedescendant and aria-autocomplete DO apply
        // to textbox, and the live region below announces the open panel.
        aria-controls={panelOpen ? 'mention-listbox' : undefined}
        aria-autocomplete="list"
        aria-activedescendant={panelOpen && rows.length > 0 ? `mention-opt-${activeIndex}` : undefined}
      />

      {panelOpen && draft && (
        <MentionPanel
          groups={groups}
          rows={rows}
          activeIndex={activeIndex}
          selected={draft.values}
          loading={store.mentionValuesLoading}
          channels={store.mentionChannels}
          onSelect={applySelection}
          onExpand={(category) => setDraft({
            ...draft,
            expanded: {
              ...draft.expanded,
              [category]: nextExpandedCap(draft.expanded[category]),
            },
          })}
          onHoverRow={(index) => setDraft({ ...draft, activeIndex: index })}
        />
      )}

      <div aria-live="polite" className={styles.srOnly}>
        {panelOpen ? `${rows.length} suggestions` : ''}
      </div>
    </div>
  );
}));

export default MentionComposer;
